import { TranslateService } from '@ngx-translate/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import * as _ from 'lodash';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { NotificationDataInstanceToken } from '../../interfaces/notification-data.interface';
import { TimeRecordingService } from '../../services/time-recording/time-recording.service';
import { ValueHelperInstanceToken } from '../../interfaces/value-helper.interface';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import { ODataUtilities } from '../../utilities/odata-utilities';
import { ArrayModel } from '../complex/array.model';
import { CodeModel } from '../complex/code.model';
import { CompositeModel } from '../complex/composite.model';
import { DropdownModel } from '../complex/dropdown.model';
import { EquipmentModel } from '../complex/equipment.model';
import { ODataQualityCodeOutModel, QualityCodeModel } from '../complex/quality-code.model';
import { RadioModel } from '../complex/radio.model';
import { NotificationModel } from '../low-level/notification.model';
import { StatusModel } from '../low-level/status.model';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { DateModelOptions } from '../options/date-model-options';
import { DropdownModelOptions } from '../options/dropdown-model-options';
import { TimeModelOptions } from '../options/time-model-options';
import { BooleanModel } from '../primitive/boolean.model';
import { DateModel } from '../primitive/date.model';
import { MultilineTextModel, ODataTextLineOutModel } from '../primitive/multiline-text.model';
import { ODataTextOutModel, TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { TreeViewModel } from '../primitive/tree-view.model';
import { CrmCodeModel, ODataCrmCodeOutModel } from './crm-code.model';
import { TimeRecordingEntryModel } from './time-recording-entry.model';
import { SHOW_APPROVER_TRANSITIONS } from './approver-transitions.constants';

export class ConfirmationEquipmentModel extends CompositeModel {
    private readonly configService: ConfigInterface;

    public equipmentNr: TextModel;
    public equipmentTechId: TextModel;
    public equipmentMatnr: TextModel;
    public equipmentSernr: TextModel;
    public equipments: DropdownModel<EquipmentModel>;
    public statusInArrival: DropdownModel<TextModel>;
    public statusAfterFix: DropdownModel<TextModel>;
    public downTimeStartDate: DateModel;
    public downTimeStartTime: TimeModel;
    public timeOfRecoveryDate: DateModel;
    public timeOfRecoveryTime: TimeModel;
    public reportCodes: DropdownModel<TextModel>;
    public reportTextLong: MultilineTextModel;
    public showInfoToCCC: boolean;
    public infoToCCC: MultilineTextModel;
    public commercialInfoCodes: DropdownModel<TextModel>;
    public commercialInfoDesc: TextModel;
    public reasonsCode: TreeViewModel<CodeModel> | undefined;
    public reasonsCodeFallback: DropdownModel<TextModel>;
    public readonly assayIndicator = ConfigConstants.assayIndicator;
    public assayNumber: MultilineTextModel;
    public lotNumber: MultilineTextModel;
    public legalCheckQualityControlQuestionText: string;
    public legalCheckQualityControlQuestionRadio: RadioModel<TextModel>;
    public qualityCode: QualityCodeModel;
    public qualityCodes: ArrayModel<QualityCodeModel>;
    public statusAfterFixValueForQCFlag: string;
    public criticality: string;
    private specialInfoCode: string[];
    public troubleTextLong: MultilineTextModel;
    public maxOneSelection = false;
    public srsVpnConnection?: DropdownModel<TextModel>;
    public checkInOutName?: TextModel;
    public checkInOutPhone?: TextModel;
    public showCommercialInfo: boolean;
    public isCriticalityChangeEnabled: boolean;
    public showApprover: boolean;
    public approver?: TextModel;
    public affectedEquipment = '';
    public phiDataCode: DropdownModel<TextModel>;
    public downtimeDurationAcknowledged: DisplayDecorator<BooleanModel>;
    public noDowntimeDurationFlag: DisplayDecorator<BooleanModel>;
    private translate: TranslateService;
    public isAppointmentBooked = false;
    public readonly hideRecoveryTime: boolean;
    private isCentriCareContract: boolean;

    public constructor(
        notification: NotificationModel,
        timeRecordingEntries: TimeRecordingEntryModel[],
        equipmentStatusInArrival: ValueHelperModel[],
        equipmentStatusAfterFix: ValueHelperModel[],
        equipmentErrorCodes: ValueHelperModel[],
        equipmentSalesInfo: ValueHelperModel[],
        equipmentCommercialInfoDefaultCode: ValueHelperModel[],
    ) {
        super('ConfirmationEquipmentModel');
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const valueHelperService = injector.get(ValueHelperInstanceToken);
        const notificationService = injector.get(NotificationDataInstanceToken);
        const notificationUtilities = injector.get(NotificationUtilities);
        this.translate = injector.get(TranslateService);
        this.configService = injector.get(ConfigInstanceToken);
        const timeRecordingService = injector.get(TimeRecordingService);

        const isNotificationRemoteWork = notificationService.isNotificationRemoteWork(notification);
        this.hideRecoveryTime = this.configService.getHideRecoveryTimeForNotificationTypes(
            notification.notificationType,
            notificationUtilities.getNotificationCategory(notification),
        );

        this.isCriticalityChangeEnabled = this.configService.isCriticalityApplicableOnNotifType(
            notification.notificationType,
        );
        if (this.isCriticalityChangeEnabled) {
            this.isCentriCareContract = notification.isCentriCareContract();
            this.criticality = notificationUtilities.getNotificationStatusInArrivalDescription(notification);

            // Create a text model for the approver
            this.approver = new TextModel('Approver');
            this.approver.options = {
                ...this.approver.options,
                required: true,
            };
        }

        // CCB : https://evorait.atlassian.net/wiki/spaces/SHC/pages/641761472/CCB+meeting+09+2019
        // Issue ticket : https://evorait.atlassian.net/browse/SHCNGMAAP2-658
        // An equipment can have multiple measuring points, it is 1:n relation.
        // All the countries follow this rule and in China we found this defect because there equipment have
        // multiple measuring points.
        // At the gateway in the NDP structure measuring points will be linked to one equipment, this cannot be changed
        // and hence we see duplicate entries in EquipmentSet
        // To handle this the UI we get the unique array values from EquipmentSet
        const equipments = _.uniqBy(notification.equipmentSet, 'equnr.value');
        const dropdownEntries = equipments.map(
            (equipment: EquipmentModel) =>
                new DisplayDecorator<EquipmentModel>(equipment.equiShortDesc.value, equipment),
        );
        this.equipments = new DropdownModel<EquipmentModel>('EquipmentDropdown', dropdownEntries);
        modelOptionsLoader.load(this.equipments, 'Equipment', 'Equipment');

        const mainEquipment = notification.getMainEquipment();
        if (!mainEquipment) {
            throw new Error(
                `ConfirmationEquipmentModel: No Main Equipment available for notification '${notification.identifier.toString()}'`,
            );
        }

        this.equipments.selectByPredicate((entry) => entry.model.equnr.value === mainEquipment.equnr.value);

        this.affectedEquipment = notification.getAffectedEquipment()?.equiShortDesc.value ?? '';

        this.equipmentNr = <TextModel>(
            this.set(new TextModel('Equipment', this.equipments.selectedEntry.model.equnr.value))
        );
        this.equipmentMatnr = <TextModel>(
            this.set(new TextModel('EquiMatnr', this.equipments.selectedEntry.model.matnr.value))
        );
        this.equipmentSernr = <TextModel>(
            this.set(new TextModel('EquiSernr', this.equipments.selectedEntry.model.sernr.value))
        );
        this.equipmentTechId = <TextModel>(
            this.set(new TextModel('EquiKSNum', this.equipments.selectedEntry.model.tidnr.value))
        );

        const equipmentStatusInArrivalEntries = equipmentStatusInArrival.map(
            (vh: ValueHelperModel) =>
                new DisplayDecorator<TextModel>(vh.descText, new TextModel('ArrivalStatus', vh.code)),
        );
        this.statusInArrival = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('ArrivalStatus', equipmentStatusInArrivalEntries))
        );
        modelOptionsLoader.load(this.statusInArrival, 'Equipment', 'StatusInArrival');

        const defaultStatusInArrival = this.hideRecoveryTime
            ? ConfigConstants.EQUIPMENT_STATUS.WORKING
            : notification.effect
              ? notification.effect.toString()
              : '';
        if (defaultStatusInArrival.length > 0) {
            this.statusInArrival.selectByValue(defaultStatusInArrival);
        }

        const equipmentStatusAfterFixEntries = equipmentStatusAfterFix.map(
            (vh: ValueHelperModel) =>
                new DisplayDecorator<TextModel>(vh.descText, new TextModel('ServiceStatus', vh.code)),
        );
        this.statusAfterFix = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('ServiceStatus', equipmentStatusAfterFixEntries))
        );
        modelOptionsLoader.load(this.statusAfterFix, 'Equipment', 'StatusAfterFix');
        this.statusAfterFix.selectByPredicate(
            (option) => this.hideRecoveryTime && option.value === ConfigConstants.EQUIPMENT_STATUS.WORKING,
        );

        const now = moment().startOf('minute');
        // if the downtime start is not given, we have to use the start of the OSR entry.
        // if we have no locally stored OSR, the app was reinstalled. In that case we use the backend status which
        // has to be OSR, otherwise we would not be in a confirmation right now.
        const osrStatus: StatusModel =
            notificationService.getNotificationStatusByStatusType(notification, NOTIFICATION_STATUS_CODE.OSR) ||
            notificationService.getStatus(notification);

        const startMalfunction = notification.startMalfunction.isValid()
            ? notification.startMalfunction
            : osrStatus.startTime;
        this.downTimeStartDate = <DateModel>(
            this.set(
                new DateModel('SystemDownStartDate', startMalfunction, new DateModelOptions({ maxValue: now.clone() })),
            )
        );
        this.downTimeStartTime = <TimeModel>(
            this.set(
                new TimeModel('SystemDownStartTime', startMalfunction, new TimeModelOptions({ maxValue: now.clone() })),
            )
        );
        const timeOfRecoveryMaxValue = timeRecordingService.getMaximumValueForTimeOfRecovery(
            timeRecordingEntries,
            isNotificationRemoteWork,
        );
        this.timeOfRecoveryDate = <DateModel>this.set(
            new DateModel(
                'SystemDownEndDate',
                moment.invalid(),
                new DateModelOptions({
                    maxValue: timeOfRecoveryMaxValue,
                    minValue: this.downTimeStartDate.value.clone(),
                }),
            ),
        );
        this.timeOfRecoveryTime = <TimeModel>this.set(
            new TimeModel(
                'SystemDownEndTime',
                moment.invalid(),
                new TimeModelOptions({
                    maxValue: timeOfRecoveryMaxValue,
                    minValue: this.downTimeStartTime.value.clone(),
                }),
            ),
        );
        // For certain notification types and categories, the downtime and recovery time should have equal value
        if (this.hideRecoveryTime) {
            const downTimeStart = this.downTimeStartDate.value.clone();
            this.timeOfRecoveryDate.value = downTimeStart;
            this.timeOfRecoveryTime.value = downTimeStart;
        }

        const noDowntimeFlag = new BooleanModel('NoDowntime');
        this.noDowntimeDurationFlag = new DisplayDecorator<BooleanModel>('', noDowntimeFlag);

        if (this.configService.getDowntimeThresholdInMinutes() !== -1) {
            const downtimeDuration = new BooleanModel('DowntimeDuration');
            this.downtimeDurationAcknowledged = new DisplayDecorator<BooleanModel>('', downtimeDuration);
        }

        const equipmentErrorCodeEntries = equipmentErrorCodes.map(
            (vh: ValueHelperModel) =>
                new DisplayDecorator<TextModel>(vh.descText, new TextModel('EquipmentErrorCode', vh.code)),
        );

        const defaultNoOfSelections = 5;

        this.reportCodes = new DropdownModel<TextModel>(
            'ErrorCode',
            equipmentErrorCodeEntries,
            new DropdownModelOptions({ maxSelections: this.maxOneSelection ? 1 : defaultNoOfSelections }),
        );
        modelOptionsLoader.load(this.reportCodes, 'Equipment', 'ReportCode');

        // Remote Work should be pre-selected if Notification is being worked remotely and cannot be unselected
        if (isNotificationRemoteWork) {
            const reportCodeForRemoteWork = this.configService.getEquipmentErrorCodeRemoteWork();
            this.reportCodes.options.alwaysSelectedEntries.push(reportCodeForRemoteWork);
            this.reportCodes.selectByPredicate((entry) => entry.value === reportCodeForRemoteWork);
        }

        // NGMAA wants the report text field to be as large as the trouble text field
        const rowAmount = 10;
        this.reportTextLong = <MultilineTextModel>this.set(new MultilineTextModel('CrmLongTextRETMSet'));
        modelOptionsLoader.load(this.reportTextLong, 'Equipment', 'ReportText');
        this.reportTextLong.options = {
            ...this.reportTextLong.options,
            rows: rowAmount,
            // Limitation set for SHSNGMAA-231 and SHCNGMP58-1954
            columns: this.configService.getReportTextLineLength(),
            lineLength: this.configService.getReportTextLineLength(),
            formatMultilineTextPattern: this.configService.getFormatMultilineTextPattern(),
            maxLines: this.configService.getReportTextMaxLines(),
            placeholder: this.configService.getShowPlaceHolderForReportText()
                ? <string>this.translate.instant('CONFIRMATION_EQUIPMENT_REPORT_TEXT_PLACEHOLDER')
                : '',
        };

        this.showInfoToCCC = this.configService.getShowInfoToCCC();
        this.infoToCCC = new MultilineTextModel('CrmLongTextSRINSet');

        this.showCommercialInfo = this.configService.getShowCommercialInfo();

        const salesInfoEntries = equipmentSalesInfo.map(
            (vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel('EquipmentSalesInfo', vh.code)),
        );
        this.commercialInfoCodes = new DropdownModel<TextModel>(
            'BkInfoCode',
            salesInfoEntries,
            new DropdownModelOptions({ maxSelections: 5 }),
        );
        modelOptionsLoader.load(this.commercialInfoCodes, 'Equipment', 'SalesInfoCode');
        this.commercialInfoDesc = <TextModel>this.set(new TextModel('BkInfoText'));
        modelOptionsLoader.load(this.commercialInfoDesc, 'Equipment', 'SalesInfoDesc');
        // if the notification got cancelled then update the BKInfoText value and disable field so user cannot enter anything
        // for country like China which do not need CommercialInfo the value should not be set
        if (notification.backendStatus.statusCode === NOTIFICATION_STATUS_CODE.CANC && this.showCommercialInfo) {
            this.commercialInfoDesc.value = this.configService.getCIDefaultText();
            this.commercialInfoDesc.options = {
                ...this.commercialInfoDesc.options,
                readonly: true,
            };
            this.specialInfoCode = equipmentCommercialInfoDefaultCode.map((vh) => vh.code);
        }

        if (!this.configService.getDisplayTrendCodeInFollowup()) {
            this.reasonsCode = notificationUtilities.getEquipmentErrorCodes(notification);
            if (this.reasonsCode) {
                modelOptionsLoader.load(this.reasonsCode, 'Equipment', 'Reason');
            }

            this.assayNumber = new MultilineTextModel('CrmLongTextASSASet');
            modelOptionsLoader.load(this.assayNumber, 'Equipment', 'AssayNumber');
            this.assayNumber.options = {
                ...this.assayNumber.options,
                applicationIdentifier: ConfigConstants.ApplicationIdentifier.ProductIdentifier,
            };
            this.lotNumber = new MultilineTextModel('CrmLongTextLOTNSet');
            modelOptionsLoader.load(this.lotNumber, 'Equipment', 'LotNumber');
            this.lotNumber.options = {
                ...this.lotNumber.options,
                applicationIdentifier: ConfigConstants.ApplicationIdentifier.LotNumber,
            };
        }

        this.statusAfterFixValueForQCFlag = this.configService.getStatusAfterFixValueForQCFlag();

        // Quality control questions (NGM AA)
        this.qualityCode = new QualityCodeModel('QCPD', '0');
        this.qualityCodes = <ArrayModel<QualityCodeModel>>(
            this.set(new ArrayModel<QualityCodeModel>('QualityCodeSet', [this.qualityCode]))
        );

        this.legalCheckQualityControlQuestionText = valueHelperService
            .getQualityControlQuestions() // this is an array of valuehelper models which we need to join
            .map((vh) => vh.descText) // then extract the text pieces
            .join(''); // and join them into one string
        const qualityControlQuestionOptions: Array<DisplayDecorator<TextModel>> = [];
        qualityControlQuestionOptions.push(
            new DisplayDecorator(
                <string>this.translate.instant('CONFIRMATION_EQUIPMENT_QUALITY_CONTROL_QUESTION_YES'),
                new TextModel(ConfigConstants.optionYes, ConfigConstants.optionValueYes),
            ),
        );
        qualityControlQuestionOptions.push(
            new DisplayDecorator(
                <string>this.translate.instant('CONFIRMATION_EQUIPMENT_QUALITY_CONTROL_QUESTION_NO'),
                new TextModel(ConfigConstants.optionNo, ConfigConstants.optionValueNo),
            ),
        );
        qualityControlQuestionOptions.push(
            new DisplayDecorator(
                <string>this.translate.instant('CONFIRMATION_EQUIPMENT_QUALITY_CONTROL_QUESTION_NA'),
                new TextModel(ConfigConstants.optionNA, ConfigConstants.optionValueNA),
            ),
        );

        // Display data medium legal questions only for in-vitro notification of type "Installation" with subtype "De-installation".
        // This should also be configurable from gateway
        const enableDataMedium =
            notification.processIndicator === ConfigConstants.PROCESS_INDICATOR.INVITRO &&
            this.configService.getShowDataMediumInEquipment() &&
            notification.notificationType === ConfigConstants.NotificationTypes.Installation &&
            notification.codeSet.some(
                (code) =>
                    code.codeType.value === ConfigConstants.CodeTypeForActivity &&
                    code.codeGroup.value === ConfigConstants.CodeGroupForDeInstallation,
            );
        if (enableDataMedium) {
            this.phiDataCode = new DropdownModel<TextModel>('CodeGroup', valueHelperService.getMaterialPhiDataCode());
            modelOptionsLoader.load(this.phiDataCode, 'Equipment', 'PHIData');
        }
    }

    /**
     * Date and time must be validated to the actual date and time of the iPad
     * and not to the date and time when the CRM was started.
     *
     * @param maxValueForTimeOfRecovery The maximum value for the time of recovery.
     */
    public setTimeOptions(maxValueForTimeOfRecovery: moment.Moment = moment.invalid()): void {
        const now = moment().startOf('minute');
        // we store the current reference for Recovery Date and time to restore them later
        const currentTimeOfRecoveryTime = this.timeOfRecoveryTime.value;

        this.timeOfRecoveryDate = assignModelOptions(
            this.timeOfRecoveryDate,
            {
                ...this.timeOfRecoveryDate.options,
                minValue: this.downTimeStartDate.value.isAfter(now) ? now : this.downTimeStartDate.value.clone(),
                readonly: this.noDowntimeDurationFlag.model.value,
            },
            this,
        );
        this.timeOfRecoveryTime = assignModelOptions(
            this.timeOfRecoveryTime,
            {
                ...this.timeOfRecoveryTime.options,
                minValue: this.downTimeStartTime.value.isAfter(now) ? now : this.downTimeStartTime.value.clone(),
                readonly: this.noDowntimeDurationFlag.model.value,
            },
            this,
        );

        if (maxValueForTimeOfRecovery.isValid()) {
            this.timeOfRecoveryDate = assignModelOptions(
                this.timeOfRecoveryDate,
                {
                    ...this.timeOfRecoveryDate.options,
                    maxValue: maxValueForTimeOfRecovery,
                    readonly: this.noDowntimeDurationFlag.model.value,
                },
                this,
            );
            this.timeOfRecoveryTime = assignModelOptions(
                this.timeOfRecoveryTime,
                {
                    ...this.timeOfRecoveryTime.options,
                    maxValue: maxValueForTimeOfRecovery,
                    readonly: this.noDowntimeDurationFlag.model.value,
                },
                this,
            );
        }

        // To ensure that both models stay in sync, we restore the previous reference for the time of recovery date and time
        this.timeOfRecoveryTime.value = currentTimeOfRecoveryTime;
        this.timeOfRecoveryDate.value = currentTimeOfRecoveryTime;

        this.downTimeStartDate = assignModelOptions(
            this.downTimeStartDate,
            {
                ...this.downTimeStartDate.options,
                readonly: this.noDowntimeDurationFlag.model.value,
                maxValue: now,
            },
            this,
        );

        this.downTimeStartTime = assignModelOptions(
            this.downTimeStartTime,
            {
                ...this.downTimeStartTime.options,
                readonly: this.noDowntimeDurationFlag.model.value,
                maxValue: now,
            },
            this,
        );
    }

    public getOutModel(): ODataConfirmationEquipmentOutModel {
        // Update entity for quality code.
        // Noteworthy: While the code and value for this entity are shared between
        // For P58, this depends on the status-after-fix of an equipment.
        this.qualityCode.chosenOption.value =
            this.statusAfterFix.selectedEntry?.value === this.statusAfterFixValueForQCFlag ? '1' : '0';

        // get the outmodel
        const crmCodes = <ArrayModel<CrmCodeModel>>this.set(new ArrayModel<CrmCodeModel>('CrmCodesSet'));
        const outModel = <ODataConfirmationEquipmentOutModel>super.getOutModel();

        // join the outmodel array for reportCodes. Add special code "9999" if there is a report text
        const specialReportCode = this.reportTextLong.value.length > 0 ? ['9999'] : [];

        const reportCodesOutModel = this.reportCodes.getOutModel();

        if (this.reportCodes.options.maxSelections === 1) {
            _.merge(
                outModel,
                ODataUtilities.joinAndAppendObjectOutModel(
                    // type assertion needed here as reportCodesOutModel is of type ODataObject
                    // ODataObject can have also other values than string
                    <{ [key: string]: string }>reportCodesOutModel,
                    specialReportCode,
                ),
            );
        } else {
            _.merge(
                outModel,
                ODataUtilities.joinAndAppendArrayOutModel(
                    // type assertion needed here as reportCodesOutModel is of type ODataObject
                    // ODataObject can have also other values than string
                    <{ [key: string]: string[] }>reportCodesOutModel,
                    specialReportCode,
                ),
            );
        }

        // join the outmodel array for commercialInfoCodes
        const specialCommercialInfoCode: string[] = this.specialInfoCode;
        const commercialInfoCodesOutModel = <{ [key: string]: string[] }>this.commercialInfoCodes.getOutModel();
        _.merge(
            outModel,
            ODataUtilities.joinAndAppendArrayOutModel(commercialInfoCodesOutModel, specialCommercialInfoCode),
        );

        if (!this.configService.getDisplayTrendCodeInFollowup()) {
            // if a reason code was selected, include its error code and error code group. Else send empty strings instead.
            let causeCode = new TextModel('CauseCode').getOutModel();
            let causeCodeGroup = new TextModel('CauseCodeGroup').getOutModel();
            if (this.reasonsCode && this.reasonsCode.selectedEntry) {
                causeCode = this.reasonsCode.selectedEntry.payload.codeName.getOutModel();
                causeCodeGroup = this.reasonsCode.selectedEntry.payload.codeGroup.getOutModel();
            } else if (this.reasonsCodeFallback && this.reasonsCodeFallback.selectedEntry) {
                causeCode = <ODataTextOutModel>this.reasonsCodeFallback.selectedEntry.getOutModel();
            }
            _.merge(outModel, causeCode);
            _.merge(outModel, causeCodeGroup);

            // If the error code is an assay number we need to transmit assay code and lot nr as well
            if (
                this.reasonsCode &&
                this.reasonsCode.selectedEntry &&
                this.reasonsCode.selectedEntry.payload.codeGroup.value.indexOf(this.assayIndicator) === 0
            ) {
                _.merge(outModel, this.assayNumber.getOutModel());
                _.merge(outModel, this.lotNumber.getOutModel());
            }
        }

        // Add info to CCC if the field is being displayed
        if (this.showInfoToCCC) {
            _.merge(outModel, this.infoToCCC.getOutModel());
            if (this.isAppointmentBooked) {
                const injector = CustomInjector.getInjector();
                const configService = injector.get(ConfigInstanceToken);
                const itcsText = configService.getAppBookingITCS();

                if (itcsText.length > 0) {
                    outModel.CrmLongTextSRINSet.push({
                        Counter: outModel.CrmLongTextSRINSet.length + 1,
                        TextLine: itcsText,
                    });
                }
            }
        }

        if (this.phiDataCode?.selectedEntry) {
            crmCodes.addModel(
                new CrmCodeModel({
                    codeType: 'M',
                    codeGroup: 'IEC6',
                    code: this.phiDataCode.selectedEntry.value,
                    codeText: this.equipmentNr.value,
                }),
            );
        }

        // Get first 300 characters out of ReportTextLongTextSet and add as text model 'ReportText'
        const characterAmount = 300;
        const reportTextShort = new TextModel('ReportText', this.reportTextLong.value.substr(0, characterAmount));
        _.merge(outModel, reportTextShort.getOutModel());

        if (this.showApprover && this.approver && this.approver.value) {
            crmCodes.addModel(
                new CrmCodeModel({
                    codeType: 'A',
                    codeGroup: 'YCCU',
                    code: 'APPR',
                    codeText: `Customer Approver: ${this.approver.value}`,
                    startDateTime: this.downTimeStartDate.value.clone(),
                    endDateTime: this.timeOfRecoveryDate.value.clone(),
                }),
            );
            crmCodes.addModel(
                new CrmCodeModel({
                    codeType: 'A',
                    codeGroup: 'YCCU',
                    code: 'COEC',
                    codeText: `${this.statusInArrival.selectedEntry?.value ?? ''}-${this.statusAfterFix.selectedEntry?.value ?? ''}`,
                    startDateTime: this.downTimeStartDate.value.clone(),
                    endDateTime: this.timeOfRecoveryDate.value.clone(),
                }),
            );
        }
        _.merge(outModel, crmCodes.getOutModel());
        return outModel;
    }

    public onEquipmentChange(): void {
        this.equipmentNr.value = this.equipments.selectedEntry.model.equnr.value;
        this.equipmentMatnr.value = this.equipments.selectedEntry.model.matnr.value;
        this.equipmentSernr.value = this.equipments.selectedEntry.model.sernr.value;
        this.equipmentTechId.value = this.equipments.selectedEntry.model.tidnr.value;
    }

    public getEquipmentStatus(): EquipmentStatus {
        const equipmentStatus: EquipmentStatus = {};
        if (this.statusAfterFix.selectedEntry) {
            equipmentStatus.equipmentAfterFix = this.statusAfterFix.selectedEntry;
        }
        return equipmentStatus;
    }

    /**
     * Update the visibility of the approver based on the current transition.
     *
     * The current transition is determined by the status in arrival and status after fix.
     * The transitions are divided into two lists: showApproverTransitions and hideApproverTransitions
     * If the current transition is in showApproverTransitions, the approver is shown.
     * If the current transition is in hideApproverTransitions, the approver will not be shown.
     * Otherwise, the approver will be hidden.
     */
    public updateApproverVisibility(): void {
        // Hide approver by default.
        this.showApprover = false;

        // Determine the current transition based on notification effect and selected entry value.
        const transition: [string, string] = [
            this.statusInArrival.selectedEntry?.value ?? '',
            this.statusAfterFix.selectedEntry?.value ?? '',
        ];

        // Check if the current transition is in the list of showApproverTransitions or hideApproverTransitions.
        if (this.isCentriCareContract) {
            if (SHOW_APPROVER_TRANSITIONS.some(([from, to]) => from === transition[0] && to === transition[1])) {
                this.showApprover = true;
            }
        }
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns the json representation of the model
     */
    public toJSON(): unknown {
        return {
            assayNumber: this.assayNumber ? this.assayNumber.value : undefined,
            commercialInfoCodes: this.commercialInfoCodes.selectedEntries.map((entry) => entry.value),
            commercialInfoDesc: this.commercialInfoDesc.value,
            isCommercialInfoRequired: this.commercialInfoDesc.options.required,
            downTimeStartDateTime: this.downTimeStartDate.value,
            selectedEquipment: this.equipments.selectedEntry.model.equnr.value,
            infoToCCC: this.infoToCCC.value,
            legalCheckQualityControlQuestionRadio: this.legalCheckQualityControlQuestionRadio
                ? this.legalCheckQualityControlQuestionRadio.selectedEntry.name
                : '',
            lotNumber: this.lotNumber ? this.lotNumber.value : undefined,
            reasonsCode: this.reasonsCode
                ? this.reasonsCode.selectedEntries.map(
                      (node) => `${node.id}${ConfigConstants.stringSeparator}${node.value}`,
                  )
                : undefined,
            reasonsCodeFallback: this.reasonsCodeFallback
                ? this.reasonsCodeFallback.selectedEntries.map((entry) => entry.value)
                : undefined,
            reportCodes: this.reportCodes.selectedEntries.map((entry) => entry.value),
            reportText: this.reportTextLong.value,
            statusAfterFix: this.statusAfterFix.selectedEntries.map((entry) => entry.value),
            statusInArrival: this.statusInArrival.selectedEntries.map((entry) => entry.value),
            timeOfRecoveryDateTime: this.timeOfRecoveryDate.value,
            troubleTextLong: this.troubleTextLong ? this.troubleTextLong.value : undefined,
            selectedSrsVpnConnection:
                this.srsVpnConnection && this.srsVpnConnection.selectedEntry
                    ? this.srsVpnConnection.selectedEntry.model.value
                    : undefined,
            checkInOutName: this.checkInOutName ? this.checkInOutName.value : undefined,
            checkInOutPhone: this.checkInOutPhone ? this.checkInOutPhone.value : undefined,
            phiDataCode:
                this.phiDataCode && this.phiDataCode.selectedEntry ? this.phiDataCode.selectedEntry.value : undefined,
            downtimeDurationAcknowledged: this.downtimeDurationAcknowledged?.model.value,
            noDowntimeDurationFlag: this.noDowntimeDurationFlag?.model.value,
            approver: this.approver?.value,
            isAppointmentBooked: this.isAppointmentBooked,
        };
    }
}

export class ConfirmationEquipmentModelFromJson extends ConfirmationEquipmentModel {
    public constructor(
        data: JsonConfirmationEquipmentModel,
        notification: NotificationModel,
        timeRecordingEntries: TimeRecordingEntryModel[],
        equipmentStatusInArrival: ValueHelperModel[],
        equipmentStatusAfterFix: ValueHelperModel[],
        equipmentErrorCode: ValueHelperModel[],
        equipmentSalesInfo: ValueHelperModel[],
        equipmentCommercialInfoDefaultCode: ValueHelperModel[],
    ) {
        super(
            notification,
            timeRecordingEntries,
            equipmentStatusInArrival,
            equipmentStatusAfterFix,
            equipmentErrorCode,
            equipmentSalesInfo,
            equipmentCommercialInfoDefaultCode,
        );
        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigInstanceToken);
        if (data.assayNumber && this.assayNumber) {
            this.assayNumber.value = data.assayNumber;
        }
        data.commercialInfoCodes.forEach((value) => {
            this.commercialInfoCodes.selectByPredicate((code) => code.value === value);
        });
        this.commercialInfoDesc.value =
            notification.backendStatus.statusCode === NOTIFICATION_STATUS_CODE.CANC
                ? configService.getCIDefaultText()
                : data.commercialInfoDesc;

        this.commercialInfoDesc.options = {
            ...this.commercialInfoDesc.options,
            required: data.isCommercialInfoRequired ? data.isCommercialInfoRequired : false,
        };
        const downTimeStartDateTime = data.downTimeStartDateTime
            ? moment(data.downTimeStartDateTime)
            : moment.invalid();
        this.downTimeStartDate.value = downTimeStartDateTime;
        this.downTimeStartTime.value = downTimeStartDateTime;
        this.equipments.selectByPredicate((equipment) => equipment.model.equnr.value === data.selectedEquipment);
        this.onEquipmentChange();
        this.infoToCCC.value = data.infoToCCC;
        this.isAppointmentBooked = data.isAppointmentBooked;
        if (this.legalCheckQualityControlQuestionRadio) {
            this.legalCheckQualityControlQuestionRadio.selectByPredicate(
                (entry) => entry.name === data.legalCheckQualityControlQuestionRadio,
            );
        }
        if (data.lotNumber && this.lotNumber) {
            this.lotNumber.value = data.lotNumber;
        }
        if (data.reasonsCode && this.reasonsCode) {
            data.reasonsCode.forEach((label) => {
                const nodeId = label.split(ConfigConstants.stringSeparator)[0];
                const nodeTitle = label.split(ConfigConstants.stringSeparator)[1];
                if (this.reasonsCode) {
                    this.reasonsCode.selectEntryByIdAndTitle(nodeId, nodeTitle);
                } else {
                    throw new Error(
                        'Could not re-create confirmation equipment model from JSON due to missing matching reasonscode entry!',
                    );
                }
            });
        }

        if (data.reasonsCodeFallback && this.reasonsCodeFallback) {
            data.reasonsCodeFallback.forEach((value) => {
                this.reasonsCodeFallback.selectByPredicate((code) => code.value === value);
            });
        }

        data.reportCodes.forEach((value) => {
            this.reportCodes.selectByPredicate((entry) => entry.value === value);
        });
        this.reportTextLong.value = data.reportText;
        data.statusAfterFix.forEach((value) => {
            this.statusAfterFix.selectByPredicate((entry) => entry.value === value);
        });
        data.statusInArrival.forEach((value) => {
            this.statusInArrival.selectByPredicate((entry) => entry.value === value);
        });
        const timeOfRecoveryDateTime = data.timeOfRecoveryDateTime
            ? moment(data.timeOfRecoveryDateTime)
            : moment.invalid();
        this.timeOfRecoveryDate.value = timeOfRecoveryDateTime;
        this.timeOfRecoveryTime.value = timeOfRecoveryDateTime;
        if (configService.getDowntimeThresholdInMinutes() !== -1) {
            this.downtimeDurationAcknowledged.model.value = data.downtimeDurationAcknowledged;
        }
        this.setTimeOptions();
        this.noDowntimeDurationFlag.model.value = data.noDowntimeDurationFlag;
        if (this.approver) {
            this.approver.value = data.approver;
        }
        if (data.troubleTextLong) {
            this.troubleTextLong.value = data.troubleTextLong;
        }

        if (this.srsVpnConnection && data.selectedSrsVpnConnection) {
            this.srsVpnConnection.selectByPredicate((entry) => entry.value === data.selectedSrsVpnConnection);
        }
        if (this.checkInOutName && data.checkInOutName) {
            this.checkInOutName.value = data.checkInOutName;
        }
        if (this.checkInOutPhone && data.checkInOutPhone) {
            this.checkInOutPhone.value = data.checkInOutPhone;
        }
        if (this.phiDataCode && data.phiDataCode) {
            this.phiDataCode.selectByPredicate((entry) => entry.value === data.phiDataCode);
        }
    }
}

export interface JsonConfirmationEquipmentModel {
    assayNumber: string | undefined;
    commercialInfoCodes: string[];
    commercialInfoDesc: string;
    isCommercialInfoRequired: boolean;
    downTimeStartDateTime: string;
    selectedEquipment: string;
    infoToCCC: string;
    legalCheckQualityControlQuestionRadio: string;
    lotNumber: string | undefined;
    reasonsCode: string[];
    reasonsCodeFallback: string[];
    reportCodes: string[];
    reportText: string;
    statusAfterFix: string[];
    statusInArrival: string[];
    timeOfRecoveryDateTime: string;
    troubleTextLong: string;
    selectedSrsVpnConnection: string;
    checkInOutName: string;
    checkInOutPhone: string;
    phiDataCode: string;
    downtimeDurationAcknowledged: boolean;
    noDowntimeDurationFlag: boolean;
    approver: string;
    isAppointmentBooked: boolean;
}

export interface ODataConfirmationEquipmentOutModel extends ODataObject {
    ArrivalStatus: string;
    BkInfoCode: string;
    BkInfoText: string;
    CauseCode: string;
    CauseCodeGroup: string;
    CrmLongTextRETMSet: ODataTextLineOutModel[];
    CrmLongTextSRINSet: ODataTextLineOutModel[];
    CrmLongTextASSASet: ODataTextLineOutModel[];
    CrmLongTextLOTNSet: ODataTextLineOutModel[];
    CrmLongTextTRTESet: ODataTextLineOutModel[];
    CrmCodesSet: ODataCrmCodeOutModel[];
    Equipment: string;
    EquiKSNum: string;
    EquiMatnr: string;
    EquiSernr: string;
    ErrorCode: string;
    EquipmentSalesInfo: string;
    QualityCodeSet: ODataQualityCodeOutModel[];
    ReportText: string;
    ServiceStatus: string;
    SystemDownStartDate: string;
    SystemDownStartTime: string;
    SystemDownEndDate: string;
    SystemDownEndTime: string;
}

export interface EquipmentStatus {
    equipmentAfterFix?: DisplayDecorator<TextModel>;
    equipmentInArrival?: DisplayDecorator<TextModel>;
    timeOfRecoveryDate?: DateModel;
}
